"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  alpha,
  Tooltip,
  InputAdornment,
  Modal,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Autocomplete,
} from "@mui/material"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu"
import KitchenIcon from "@mui/icons-material/Kitchen"
import SaveIcon from "@mui/icons-material/Save"
import InventoryIcon from "@mui/icons-material/Inventory"
import ScaleIcon from "@mui/icons-material/Scale"
import VisibilityIcon from "@mui/icons-material/Visibility"
import EditIcon from "@mui/icons-material/Edit"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import FilterListIcon from "@mui/icons-material/FilterList"
import ClearIcon from "@mui/icons-material/Clear"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowBack"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"

import dayjs from "dayjs"
import CustomPagination from "../transaccionComponent/CustomPagination.js"

// Tema personalizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
        },
      },
    },
  },
})

const TablaReceta = () => {
  const {
    getRecetas,
    isNavbarCollapsed,
    getItemTresLetras,
    arrayItemTresLetras,
    itemTresLetras,
    itemAtributoNoEliminados,
    getItemAtributoNoEliminados,
    crearRecetaApi,
  } = useContext(ErpContext)
  const [recetas, setRecetas] = useState([])
  const [filteredRecetas, setFilteredRecetas] = useState([])
  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedReceta, setSelectedReceta] = useState(null)
  const [editedIngredientes, setEditedIngredientes] = useState([])

  const today = dayjs()
  const [startDate, setStartDate] = useState(today.subtract(30, "day"))
  const [endDate, setEndDate] = useState(today)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)

  // Estados para filtros
  const [searchTextReceta, setSearchTextReceta] = useState("")
  const [searchTextMateriaPrima, setSearchTextMateriaPrima] = useState("")
  const [filtroProducto, setFiltroProducto] = useState("")
  const [filtroIngrediente, setFiltroIngrediente] = useState("")

  const [recetaSelected, setRecetaSelected] = useState(null)
  const [materiaPrimaSelected, setMateriaPrimaSelected] = useState(null)

  // Funciones para manipular fechas
  const increaseDate = () => setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))

  useEffect(() => {
    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)
    fetchData(startDate, endDate, null, null, page, limit)
  }, [])

  // Efecto para búsqueda de ítems
  useEffect(() => {
    if (searchTextReceta.length >= 3) {
      arrayItemTresLetras([])
      getItemTresLetras(searchTextReceta)
    }
  }, [searchTextReceta])

  // Efecto para búsqueda de ítems
  useEffect(() => {
    if (searchTextMateriaPrima.length >= 3) {
      arrayItemTresLetras([])
      getItemTresLetras(searchTextMateriaPrima)
    }
  }, [searchTextMateriaPrima])

  // Función para generar etiqueta de ítem
  const generateRecetaLabel = (item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)

    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")

    return `${item.codigo} - ${item.descripcion} ${attributeValues} ${item.codigoScanner}`
  }

  const generateMateriaPrimaLabel = (item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)

    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")

    return `${item.codigo} - ${item.descripcion} ${attributeValues} ${item.codigoScanner}`
  }

  // Función para manejar cambio de ítem
  const handleRecetaChange = (event, newValue) => {
    setRecetaSelected(newValue)
    if (newValue) {
      const defaultValues = itemAtributoNoEliminados.map((attr, index) => ({
        value: newValue[`itemDatoAtributo${index + 1}`] || "",
      }))
    }
  }

  const handleMateriaPrimaChange = (event, newValue) => {
    setMateriaPrimaSelected(newValue)
    if (newValue) {
      const defaultValues = itemAtributoNoEliminados.map((attr, index) => ({
        value: newValue[`itemDatoAtributo${index + 1}`] || "",
      }))
    }
  }

  const fetchInitialData = async () => {
    try {
      await getItemAtributoNoEliminados()
    } catch (error) {}
  }

  const fetchData = async (startDate, endDate, idReceta, idMateriaPrima, page, limit = 100) => {
    try {
      setLoading(true)

      const validDate = startDate ? startDate : dayjs()
      const formattedDate = validDate.format("YYYY-MM-DD")
      let formattedDate2 = endDate

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD")
      }

      const response = await getRecetas(formattedDate, formattedDate2, idReceta, idMateriaPrima, page, limit)

      console.log("Recetas obtenidas:", response)

      // Actualizar estados de paginación si la respuesta tiene estructura de paginación
      if (response.data) {
        setTotalPages(response.totalPages || 0)
        setTotalCount(response.total || 0)
        setFilteredRecetas(transformarRecetas(response.data))
        setRecetas(transformarRecetas(response.data))
      } else {
        // Si la respuesta es directamente un array
        setFilteredRecetas(transformarRecetas(response))
        setRecetas(transformarRecetas(response))
      }
    } catch (error) {
      console.error("Error fetching recetas:", error)
    } finally {
      setLoading(false)
    }
  }

  const transformarRecetas = (recetasData) => {
    return recetasData.map((receta) => {
      const totalIngredientes = receta.ingredientes?.length || 0
      const cantidadTotal = receta.ingredientes?.reduce((sum, ing) => sum + (ing.cantidad || 0), 0) || 0

      // Obtener nombres de ingredientes para búsqueda
      const nombresIngredientes =
        receta.ingredientes?.map((ing) => ing.materiaPrima?.descripcion || "").join(", ") || ""

      return {
        id: receta.id,
        descripcionProductoTerminado: receta.descripcionProductoTerminado || "Sin descripción",
        codigoProductoTerminado: receta.codigoProductoTerminado || "Sin código",
        idProductoTerminado: receta.idProductoTerminado,
        totalIngredientes,
        cantidadTotal: cantidadTotal.toFixed(2),
        ingredientes: receta.ingredientes || [],
        nombresIngredientes,
        // Agregar fechas formateadas
        fechaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("DD/MM/YYYY") : "N/A",
        fechaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("DD/MM/YYYY") : "N/A",
        horaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("HH:mm") : "N/A",
        horaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("HH:mm") : "N/A",
        // Para mostrar el primer ingrediente como referencia
        primerIngrediente: receta.ingredientes?.[0]?.materiaPrima?.descripcion || "Sin ingredientes",
        codigoPrimerIngrediente: receta.ingredientes?.[0]?.materiaPrima?.codigo || "",
      }
    })
  }

  const handleOpenModal = (receta) => {
    setSelectedReceta(receta)
    setEditedIngredientes(receta.ingredientes.map((ing) => ({ ...ing }))) // Copia profunda
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedReceta(null)
    setEditedIngredientes([])
  }

  const handleCantidadChange = (index, newCantidad) => {
    const updated = [...editedIngredientes]
    updated[index] = {
      ...updated[index],
      cantidad: Number.parseFloat(newCantidad) || 0,
    }
    setEditedIngredientes(updated)
  }

  const handleSaveChanges = async () => {
  console.log("Receta seleccionada", selectedReceta);
  console.log("Guardando cambios:", editedIngredientes);

  try {
    setLoading(true);

    const ingredientesConCantidad = editedIngredientes.map((item) => {
      return {
        idItem: item.idMateriaPrima,
        cantidad: item.cantidad || "0",
        idUbicacion: item.idUbicacion || 1,
      };
    });

    const idProductoTerminado = selectedReceta.id;
    console.log("ID Producto Terminado:", idProductoTerminado);
    console.log("Ingredientes con cantidad:", ingredientesConCantidad);
    await crearRecetaApi(idProductoTerminado, ingredientesConCantidad, true);

  } catch (error) {
    console.error("Error creando o actualizando receta:", error);
  } finally {
    setLoading(false);
    handleCloseModal();
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idMateriaPrima = materiaPrimaSelected ? materiaPrimaSelected.id : null
    fetchData(startDate, endDate, idReceta, idMateriaPrima, page, limit)
  }
};

  const limpiarFiltros = () => {
    setSearchTextReceta("")
    setSearchTextMateriaPrima("")
    setRecetaSelected(null)
    setMateriaPrimaSelected(null)
    setFiltroProducto("")
    setFiltroIngrediente("")
    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)
  }

  const aplicarFiltros = () => {
    console.log(recetaSelected)
    console.log(materiaPrimaSelected)
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idMateriaPrima = materiaPrimaSelected ? materiaPrimaSelected.id : null
    fetchData(startDate, endDate, idReceta, idMateriaPrima, page, limit)
  }

  const hayFiltrosActivos = searchTextReceta || searchTextMateriaPrima || recetaSelected || materiaPrimaSelected

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.2,
      minWidth: 80,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          ID
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Chip label={params.value} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
        </Box>
      ),
    },
    {
      field: "codigoProductoTerminado",
      headerName: "Código",
      flex: 0.3,
      minWidth: 120,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Código
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Chip label={params.value} size="small" color="secondary" variant="filled" sx={{ fontWeight: 600 }} />
        </Box>
      ),
    },
    {
      field: "descripcionProductoTerminado",
      headerName: "Receta",
      flex: 0.8,
      minWidth: 200,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Descripción
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <RestaurantMenuIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "totalIngredientes",
      headerName: "Ingredientes",
      flex: 0.3,
      minWidth: 120,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Ingredientes
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Badge badgeContent={params.value} color="info">
            <InventoryIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
          </Badge>
        </Box>
      ),
    },
    {
      field: "cantidadTotal",
      headerName: "Cantidad",
      flex: 0.3,
      minWidth: 100,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Cantidad
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ScaleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "fechaCreacion",
      headerName: "Fecha Creación",
      flex: 0.3,
      minWidth: 120,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          F. Creación
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
            {params.row.horaCreacion}
          </Typography>
        </Box>
      ),
    },
    {
      field: "fechaActualizacion",
      headerName: "Fecha Actualización",
      flex: 0.3,
      minWidth: 130,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          F. Actualización
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
            {params.row.horaActualizacion}
          </Typography>
        </Box>
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 0.2,
      minWidth: 80,
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Acciones
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <Tooltip title="Ver detalles de la receta">
            <IconButton
              size="small"
              onClick={() => handleOpenModal(params.row)}
              sx={{
                bgcolor: alpha(theme.palette.info.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.info.main, 0.2),
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <VisibilityIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p:1, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>

        {/* Filtros unificados */}
        <Paper sx={{ p: 1, borderRadius: 2, mb: 2, backgroundColor: "#f5f5f5",
                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)", }}>

          <Grid container spacing={2} alignItems="end">
            {/* Filtros de fecha */}
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}>
                Fecha Desde
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconButton onClick={decreaseDate} color="primary" size="small">
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    format="DD/MM/YYYY"
                    label="Desde"
                    slotProps={{
                      textField: { size: "small", sx: { width: 120 } },
                    }}
                  />
                  <IconButton onClick={increaseDate} color="primary" size="small">
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Box>
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}>
                Fecha Hasta
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Hasta"
                  slotProps={{
                    textField: { size: "small", sx: { width: 120 } },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}>
                Buscar Receta
              </Typography>
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                options={(itemTresLetras || []).map((it) => ({
                  label: generateRecetaLabel(it),
                  descripcion: it.descripcion,
                  codigo: it.codigo,
                  id: it.id,
                  codigoScanner: it.codigoScanner,
                }))}
                value={recetaSelected}
                onChange={handleRecetaChange}
                onInputChange={(event, newInputValue) => setSearchTextReceta(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Receta"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}>
                Buscar Materia Prima
              </Typography>
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                options={(itemTresLetras || []).map((it) => ({
                  label: generateMateriaPrimaLabel(it),
                  descripcion: it.descripcion,
                  codigo: it.codigo,
                  id: it.id,
                  codigoScanner: it.codigoScanner,
                }))}
                value={materiaPrimaSelected}
                onChange={handleMateriaPrimaChange}
                onInputChange={(event, newInputValue) => setSearchTextMateriaPrima(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Materia Prima"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InventoryIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Botones de acción */}
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={aplicarFiltros}
                  startIcon={<SearchIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: "#5b5b5f",
                  }}
                >
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={limpiarFiltros}
                  disabled={!hayFiltrosActivos}
                  startIcon={<ClearIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Limpiar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Estadísticas rápidas */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Chip
              icon={<RestaurantMenuIcon />}
              label={`${filteredRecetas.length} recetas`}
              color="primary"
              variant="filled"
            />
            <Chip
              icon={<InventoryIcon />}
              label={`${recetas.reduce((sum, r) => sum + r.totalIngredientes, 0)} ingredientes totales`}
              color="secondary"
              variant="outlined"
            />
            {hayFiltrosActivos && (
              <Chip
                icon={<FilterListIcon />}
                label="Filtros activos"
                color="warning"
                variant="filled"
                onDelete={limpiarFiltros}
              />
            )}
          </Box>
        </Box>

        {/* Tabla */}
        <Paper sx={{ height: 600, width: "100%" }}>
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            rows={filteredRecetas}
            columns={columns}
            getRowId={(row) => row.id}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            loading={loading}
            components={{
              Toolbar: GridToolbar,
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: false,
              },
            }}
            className="tablaVentasDataGrid"
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                //MANEJA CSS HEADER
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                //MANEJA CSS HEADER
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                //MANEJA CSS HEADER
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          />
        </Paper>

        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={filteredRecetas.length}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaRecetas"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        {/* Modal mejorado sutilmente */}
        <Modal open={openModal} onClose={handleCloseModal} aria-labelledby="modal-receta-titulo">
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "85%", lg: "75%" },
              maxWidth: 1000,
              maxHeight: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header del modal mejorado */}
            <Box
              sx={{
                p: 3,
                backgroundColor: "#5b5b5f",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RestaurantMenuIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Detalle de Receta BOM
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedReceta?.descripcionProductoTerminado} • ID: {selectedReceta?.id}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3, maxHeight: "calc(90vh - 120px)", overflowY: "auto" }}>
              {selectedReceta && (
                <>
                  {/* Información general mejorada */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <InventoryIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mb: 1 }} />
                        <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {selectedReceta.totalIngredientes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Ingredientes
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.success.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                        }}
                      >
                        <ScaleIcon sx={{ fontSize: 32, color: theme.palette.success.main, mb: 1 }} />
                        <Typography variant="h5" color="success.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {selectedReceta.cantidadTotal}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cantidad Total
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                        }}
                      >
                        <KitchenIcon sx={{ fontSize: 32, color: theme.palette.secondary.main, mb: 1 }} />
                        <Typography variant="h5" color="secondary.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {selectedReceta.idProductoTerminado}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID Producto
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.info.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: 32, color: theme.palette.info.main, mb: 1 }} />
                        <Typography variant="body1" color="info.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {selectedReceta.fechaCreacion}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fecha Creación
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Tabla de ingredientes */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <EditIcon color="primary" />
                      Ingredientes de la Receta
                    </Typography>

                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Cantidad</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {editedIngredientes.map((ingrediente, index) => (
                            <TableRow
                              key={ingrediente.idMateriaPrima}
                              sx={{
                                "&:hover": {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                },
                                "&:nth-of-type(even)": {
                                  backgroundColor: alpha(theme.palette.grey[50], 0.5),
                                },
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={ingrediente.idMateriaPrima}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 500,
                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    display: "inline-block",
                                  }}
                                >
                                  {ingrediente.materiaPrima?.codigo || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <InventoryIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {ingrediente.materiaPrima?.descripcion || "Sin descripción"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={ingrediente.cantidad}
                                  onChange={(e) => handleCantidadChange(index, e.target.value)}
                                  inputProps={{
                                    step: "0.01",
                                    min: "0",
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <ScaleIcon fontSize="small" color="action" />
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    width: 130,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                <Chip
                                  label={ingrediente.eliminado ? "Eliminado" : "Activo"}
                                  size="small"
                                  color={ingrediente.eliminado ? "error" : "success"}
                                  variant={ingrediente.eliminado ? "filled" : "outlined"}
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Botones de acción */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCloseModal}
                      startIcon={<CloseIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveChanges}
                      startIcon={<SaveIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#5b5b5f",
                        px: 3,
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Guardar Cambios
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Modal>
      </Box>
    </ThemeProvider>
  )
}

export default TablaReceta
